import { Skeleton } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import React, { useEffect, useState } from "react";
import { UserAddress } from "../../../shared/models/user/user-address";
import { changePagination, getMyAddresses } from "../../../../redux/slices/user-addresses.slice";
import { Message, MessageSections } from "../../../components/message/message";
import { SingleSelectItem } from "../../../components/single-select-item/single-select-item";
import BarekamText from "../../../components/text";
import { ToastContainer } from "react-toastify";
import { ActionBarWithSaveButton } from "../user-actionbar/action-bar-with-save-button";
import { UserAddressesContainer } from "./styled";
import BarekamPagination from "../../../orders/my/pagination/barekam-pagination";
import { EditAddress } from "./edit-address/edit-address";
import { ConfirmationDialog } from "../../../components/dialogs/confirmation-dialog";
import userAddressesService, { UserAddressUpdateMeta } from "../../../../services/user-addresses.service";
import { StandardDialog } from "../../../components/dialogs/standard-dialog/standard-dialog";
import { EditDefaultAddress } from "./edit-default-address/edit-default-address";
import { getCurrentUserData } from "../../../../redux/slices/current-user.slice";
import { SingleAddress } from "./single-address/single-address";
import { ARMENIA_COUNTRY_ID } from "../../../../constants";

export const UserAddresses = () => {
    const [addressToEdit, setAddressToEdit] = useState<UserAddress | undefined>();
    const [addressToDelete, setAddressToDelete] = useState<UserAddress>();
    const [defaultAddressDialogOpened, setDefaultAddressDialogOpened] = useState(false);

    const { myAddressesData, isLoading } = useAppSelector(state => state.userAddressesReducer);
    const { currentUser } = useAppSelector(state => state.currentUserReducer);

    const { countries } = useAppSelector(state => state.staticDataReducer);
    const armenia = countries.find(country => country.countryId === ARMENIA_COUNTRY_ID);

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(getMyAddresses({ forceLoad: false }));
    }, []);

    const onAddressEdit = (address: UserAddress) => {
        setAddressToEdit(address);
    };

    const onNewAddressClick = () => {
        setAddressToEdit({} as UserAddress);
    };

    const handleEditAddressClose = (withChange: boolean) => {
        setAddressToEdit(undefined);

        if (withChange) {
            dispatch(getMyAddresses({ forceLoad: true }));
        }
    };

    const handlePageChange = (event: unknown, newPage: number) => {
        dispatch(changePagination({ pageSize: myAddressesData.pageSize, currentPage: newPage - 1 }));
        dispatch(getMyAddresses({ forceLoad: true, page: newPage - 1 }));
    };


    const SecondaryActionText = () => (
        <BarekamText variant={"m"} bold>
            <Message messageKey={'change_default_address'} sectionId={MessageSections.USER} />
        </BarekamText>
    )

    function handleChangeDefaultAddress() {
        setDefaultAddressDialogOpened(true);
    }

    async function deleteAddress() {
        if (addressToDelete) {
            const nextDefaultAddressId = myAddressesData.addresses.find(address => address.addressId !== addressToDelete.addressId)?.addressId;

            if (nextDefaultAddressId) {
                await userAddressesService
                    .editAddress(nextDefaultAddressId, { isDefault: true })
                    .then((response: UserAddressUpdateMeta) => {
                        if (response.acknowledged) {
                            return userAddressesService.deleteAddressById(addressToDelete.addressId)
                                .then(() => {
                                    dispatch(getMyAddresses({ forceLoad: true }))
                                })
                        }
                    })
            } else {
                await userAddressesService.deleteAddressById(addressToDelete.addressId)
                    .then(() => {
                        dispatch(getMyAddresses({ forceLoad: true }))
                    })
            }
        }
    }

    const PrimaryActionText = () => (
        <>
            <BarekamText variant={"xxl"} width={'unset'}>
                +
            </BarekamText>

            <BarekamText variant={"m"} bold>
                <Message messageKey={'add_new_address'} sectionId={MessageSections.USER} />
            </BarekamText>
        </>
    )

    return (
        <>
            <ActionBarWithSaveButton secondaryActionText={<SecondaryActionText />}
                onSecondaryActionClick={handleChangeDefaultAddress}
                primaryActionText={<PrimaryActionText />}
                onPrimaryActionClick={onNewAddressClick}
            />

            <UserAddressesContainer style={{ width: '100%' }}>
                {isLoading
                    ? <>
                        <SingleSelectItem TopLabel={
                            <BarekamText variant={'m'} fontWeight={500}>
                                <Skeleton width={'100%'} height={'0.85rem'} />
                            </BarekamText>
                        }
                            LowerLabel={
                                <BarekamText variant={'m'} fontWeight={500}>
                                    <Skeleton width={'100%'} height={'0.85rem'} />
                                </BarekamText>
                            }
                            selected={false} />
                    </>
                    : <>
                        {armenia?.warehouse.map(warehouse =>
                            <SingleAddress warehouse={warehouse}
                                user={currentUser}
                                onAddressEdit={onAddressEdit}
                                setAddressToDelete={setAddressToDelete}
                                key={`warehouse-` + warehouse.warehouseId} />
                        )}
                        {myAddressesData.addresses.map(address =>
                            <SingleAddress address={address}
                                user={currentUser}
                                onAddressEdit={onAddressEdit}
                                setAddressToDelete={setAddressToDelete}
                                key={`address-` + address.addressId} />
                        )}
                    </>
                }
            </UserAddressesContainer>

            <StandardDialog headerLabel={<Message messageKey={addressToEdit?.addressId ? 'edit_address' : 'new_address'}
                sectionId={MessageSections.USER} />}
                open={!!addressToEdit}>
                <EditAddress address={addressToEdit || {} as UserAddress}
                    onSave={handleEditAddressClose} />
            </StandardDialog>

            <StandardDialog
                headerLabel={<Message messageKey={'select_default_address'} sectionId={MessageSections.USER} />}
                open={defaultAddressDialogOpened}>
                <EditDefaultAddress onClose={withChange => {
                    if (withChange) {
                        dispatch(getMyAddresses({ forceLoad: true }));
                        dispatch(getCurrentUserData());
                    }
                    setDefaultAddressDialogOpened(false);
                }} />
            </StandardDialog>

            <ConfirmationDialog open={!!addressToDelete}
                handleClose={(confirmed) => {
                    confirmed && deleteAddress();
                    setAddressToDelete(undefined);
                }}
                titleKey={'address_delete_title'}
                descriptionKey={'address_delete_description'}
                isRemove
            />

            {myAddressesData.totalItems > myAddressesData.pageSize
                ? <BarekamPagination {...myAddressesData} onPageChange={handlePageChange} />
                : <></>
            }

            <ToastContainer />
        </>
    )
}